import { EventEmitter } from 'events'
import { WebSocketMessageMode, WebSocketEventTypes } from './constants'
import { CloseEventCode } from './constants/eventCode'
import {
  WebSocketProvider,
  WebSocketClientInterface,
  WebSocketReceiverInterface,
  WebSocketMessageSubscriber,
  WebSocketEventListener,
  WebSocketMessageTypes,
  WebSocketReceiveMessage,
  WebSocketSendMessage,
  SendMessageBody
} from './types'
import CallWebSocketReceiver from './receivers/call'
import StatusWebSocketReceiver from './receivers/status'
import MessageWebSocketReceiver from './receivers/message'
import EcgWebSocketReceiver from './receivers/ecg'
import { getEnvVar, isEnv } from '@/utils'
import store from '@/store'
import Vue from 'vue'

const ENDPOINT = getEnvVar('WEB_SOCKET_ENDPOINT') as string
const IS_DEBUG = !isEnv('production')
const DEFAULT_URL = 'ws/status'

/**
 * modeごとのデータの受信をするクラスをここに追加していく。
 */
const receivers = {
  [WebSocketMessageMode.CALL]: new CallWebSocketReceiver(),
  [WebSocketMessageMode.STATUS]: new StatusWebSocketReceiver(),
  [WebSocketMessageMode.MESSAGE]: new MessageWebSocketReceiver(),
  [WebSocketMessageMode.ECG]: new EcgWebSocketReceiver()
}

const logger = (...args: any): any => {
  if (IS_DEBUG) {
    console.log('WEB_SOCKET DEBUG LOG: ', ...args)
  }
  return args
}

class WebSocketClient implements WebSocketClientInterface {
  socket!: WebSocket
  receivers = receivers
  events = new EventEmitter()

  get readyState() {
    return this.socket.readyState
  }

  constructor(socket: WebSocket) {
    this.setSocket(socket)
  }

  setSocket(socket: WebSocket) {
    this.socket = socket
    this.socket.addEventListener('message', this.handleMessage.bind(this))
    this.socket.addEventListener('close', this.handleClose.bind(this))
    this.socket.addEventListener('error', this.handleError.bind(this))
    this.socket.addEventListener('open', this.handleOpen.bind(this))
    return this
  }

  handleOpen(event: Event) {
    this.events.emit(WebSocketEventTypes.OPEN, logger(event))
  }

  handleClose(event: CloseEvent) {
    this.events.emit(WebSocketEventTypes.CLOSE, logger(event))
  }

  handleError(event: Event) {
    this.events.emit(WebSocketEventTypes.ERROR, logger(event))
  }

  handleMessage(event: MessageEvent) {
    const message = JSON.parse(event.data) as WebSocketReceiveMessage<
      WebSocketMessageMode,
      WebSocketMessageTypes<WebSocketMessageMode>
    >

    logger(message)

    this.receivers[message.mode].handleMessage(message.data as any)
    this.events.emit(WebSocketEventTypes.MESSAGE, event)
  }

  send<T extends WebSocketMessageMode>(
    mode: T,
    messageBody: SendMessageBody<T, WebSocketMessageTypes<T>>
  ) {
    const message = logger(this.createMessage(mode, messageBody))
    this.socket.send(JSON.stringify(message))
  }

  close() {
    this.socket.close()
  }

  createMessage<
    T extends WebSocketMessageMode,
    K extends WebSocketMessageTypes<T>
  >(mode: T, data: SendMessageBody<T, K>): WebSocketSendMessage<T, K> {
    return { mode, data }
  }

  addListener<T extends WebSocketEventTypes>(
    type: T,
    listener: WebSocketEventListener<T>
  ) {
    this.events.on(type, listener)

    return () => this.events.off(type, listener)
  }

  subscribeMessage<
    T extends WebSocketMessageMode,
    K extends WebSocketMessageTypes<T>
  >(mode: T, messageType: K, subscriber: WebSocketMessageSubscriber<T, K>) {
    return ((this.receivers[mode] as unknown) as WebSocketReceiverInterface<
      T
    >).subscribe(messageType, subscriber)
  }
}

const refreshToken = async () => {
  const refresh = localStorage.getItem('refresh')
  if (!refresh) {
    return
  }
  const res = await Vue.$api.auth.refresh({ refresh })

  store.commit('user/login', { token: res.access, refresh: res.refresh })
}

const WebSocketProvider: WebSocketProvider = {
  // == リミッタの設定 ==
  // 接続が切断されたら`RETRY_WAIT_TIME`待機して再試行する。
  // `RETRY_PATIENCE_TIME` ミリ秒間に `RETRY_LIMIT` 回接続に失敗したら、
  // `RETRY_LIMIT_EXPIRE` ミリ秒間再試行しない。
  RETRY_LIMIT: 3,
  RETRY_WAIT_TIME: 3000,
  RETRY_PATIENCE_TIME: 10000,
  RETRY_LIMIT_EXPIRE: 300000,

  connection: null,
  retryConnect: 0,
  retryTimeoutId: null,

  events: new EventEmitter(),

  createConnection(url = DEFAULT_URL) {
    if (this.connection) {
      return this.connection as WebSocketClientInterface
    }

    this.connection = new WebSocketClient(new WebSocket(`${ENDPOINT}${url}/`))

    this.connection.addListener(WebSocketEventTypes.OPEN, (event: Event) =>
      this.handleOpen(event)
    )
    this.connection.addListener(
      WebSocketEventTypes.CLOSE,
      (event: CloseEvent) => this.handleClose(event, url)
    )

    return this.connection as WebSocketClientInterface
  },

  destroy() {
    try {
      if (this.connection) {
        this.connection.close()
      }
    } finally {
      this.connection = null
    }
  },

  handleOpen() {
    this.retryConnect = 0
    if (this.retryTimeoutId !== null) {
      clearTimeout(this.retryTimeoutId)
      this.retryTimeoutId = null
    }
    this.events.emit(WebSocketEventTypes.OPEN, logger(event))
  },

  handleClose(event, url) {
    event = Array.isArray(event) ? event[0] : event

    switch (event.code) {
      case CloseEventCode.NORMAL_CLOSURE:
        this.destroy()
        break

      default:
        setTimeout(() => this.handleError(event, url), this.RETRY_WAIT_TIME)
        break
    }
  },

  handleError(event, url) {
    if (this.retryTimeoutId === null) {
      this.retryTimeoutId = setTimeout(() => {
        this.retryTimeoutId = null
      }, this.RETRY_PATIENCE_TIME)
    }

    this.retryConnect++

    const reinitializer = async () => {
      try {
        await refreshToken()
      } catch (error) {
        console.error(error)
      }

      const socket = new WebSocket(`${ENDPOINT}${url}/`)
      if (this.connection) {
        this.connection.setSocket(socket)
        return
      }
      this.connection = new WebSocketClient(socket)
      this.connection.addListener(WebSocketEventTypes.OPEN, (event: Event) =>
        this.handleOpen(event)
      )
      this.connection.addListener(
        WebSocketEventTypes.CLOSE,
        (event: CloseEvent) => this.handleClose(event, url)
      )
    }

    if (this.retryTimeoutId !== null && this.retryConnect > this.RETRY_LIMIT) {
      setTimeout(() => {
        this.retryConnect = 1
        reinitializer()
      }, this.RETRY_LIMIT_EXPIRE)
    } else {
      reinitializer()
    }
  }
}

export default WebSocketProvider
